import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { useParams, useNavigate } from 'react-router-dom';
import LeadFormModal from '../components/LeadFormModal';
import LossReasonModal from '../components/LossReasonModal';
import { Plus, ArrowLeft, DollarSign, Users, Search, Filter, X, Calendar, ChevronDown, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';

const LEADS_PER_LOAD = 10; // Leads to load per scroll

// Unified period options - same as Dashboard and Empresas
const PERIOD_OPTIONS = [
  { value: 'week', label: 'Esta Semana' },
  { value: 'month', label: 'Este Mes' },
  { value: 'quarter', label: 'Este Trimestre' },
  { value: 'semester', label: 'Este Semestre' },
  { value: 'year', label: 'Este Año' },
  { value: 'all', label: 'Todo el Tiempo' },
  { value: 'custom', label: 'Rango Personalizado' },
];

const KanbanBoard = () => {
  const { pipelineId } = useParams();
  const { state, actions } = useData();
  const { settings } = useSettings();
  const navigate = useNavigate();
  
  // Dynamic primary color
  const primaryColor = settings?.primaryColor || '#00b30c';
  
  // Local state for pipeline leads
  const [stageLeadsMap, setStageLeadsMap] = useState({}); // {stageId: {leads: [], hasMore: true, loading: false}}
  const [stageCounts, setStageCounts] = useState({});
  const [totalLeads, setTotalLeads] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Filters state
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [period, setPeriod] = useState('all'); // Unified period filter
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Calculate date range based on period
  const getDateRange = useCallback(() => {
    const now = new Date();
    let from = null;
    let to = null;
    
    switch (period) {
      case 'week':
        from = new Date(now);
        from.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
        break;
      case 'month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
        from = new Date(now.getFullYear(), quarterMonth, 1);
        break;
      case 'semester':
        const semesterMonth = now.getMonth() < 6 ? 0 : 6;
        from = new Date(now.getFullYear(), semesterMonth, 1);
        break;
      case 'year':
        from = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        return { dateFrom: customDateFrom || null, dateTo: customDateTo || null };
      case 'all':
      default:
        return { dateFrom: null, dateTo: null };
    }
    
    return {
      dateFrom: from ? from.toISOString().split('T')[0] : null,
      dateTo: to ? to.toISOString().split('T')[0] : null
    };
  }, [period, customDateFrom, customDateTo]);
  
  const { dateFrom, dateTo } = getDateRange();
  
  // Stage search state
  const [stageSearches, setStageSearches] = useState({});
  
  // Drag state
  const [draggedLeadId, setDraggedLeadId] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);
  
  // Modal state
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState(null);
  
  // Loss reason modal state
  const [showLossReasonModal, setShowLossReasonModal] = useState(false);
  const [pendingLossMove, setPendingLossMove] = useState(null); // { leadId, leadName, oldStageId, newStageId }

  const pipeline = state.pipelines.find(p => p.id === pipelineId);
  const searchInputRef = useRef(null);

  // Load initial leads for all stages IN PARALLEL
  const loadInitialLeads = useCallback(async () => {
    if (!pipelineId || !pipeline) return;
    
    setLoading(true);
    try {
      const stages = pipeline.stages || [];
      
      // Load all stages in parallel for faster loading
      // Pass date filters to backend for server-side filtering
      const promises = stages.map(stage => 
        api.getLeadsByStage(pipelineId, stage.id, 0, LEADS_PER_LOAD, dateFrom || null, dateTo || null)
          .then(response => ({ stageId: stage.id, response }))
          .catch(() => ({ stageId: stage.id, response: { leads: [], total: 0 } }))
      );
      
      const results = await Promise.all(promises);
      
      const newStageLeadsMap = {};
      const newStageCounts = {};
      let total = 0;
      
      results.forEach(({ stageId, response }) => {
        newStageLeadsMap[stageId] = {
          leads: response.leads || [],
          hasMore: (response.total || 0) > LEADS_PER_LOAD,
          loading: false,
          offset: LEADS_PER_LOAD,
        };
        newStageCounts[stageId] = {
          showing: response.leads?.length || 0,
          total: response.total || 0,
        };
        total += response.total || 0;
      });
      
      setStageLeadsMap(newStageLeadsMap);
      setStageCounts(newStageCounts);
      setTotalLeads(total);
    } catch (error) {
      console.error('Error loading pipeline leads:', error);
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
    }
  }, [pipelineId, pipeline, dateFrom, dateTo]);

  useEffect(() => {
    loadInitialLeads();
  }, [loadInitialLeads]);

  // Load more leads for a specific stage
  const loadMoreLeads = async (stageId) => {
    const stageData = stageLeadsMap[stageId];
    if (!stageData || stageData.loading || !stageData.hasMore) return;
    
    setStageLeadsMap(prev => ({
      ...prev,
      [stageId]: { ...prev[stageId], loading: true }
    }));
    
    try {
      const response = await api.getLeadsByStage(pipelineId, stageId, stageData.offset, LEADS_PER_LOAD, dateFrom || null, dateTo || null);
      const newLeads = response.leads || [];
      
      setStageLeadsMap(prev => ({
        ...prev,
        [stageId]: {
          leads: [...prev[stageId].leads, ...newLeads],
          hasMore: prev[stageId].offset + newLeads.length < response.total,
          loading: false,
          offset: prev[stageId].offset + LEADS_PER_LOAD,
        }
      }));
    } catch (error) {
      console.error('Error loading more leads:', error);
      setStageLeadsMap(prev => ({
        ...prev,
        [stageId]: { ...prev[stageId], loading: false }
      }));
    }
  };

  // Handle scroll for infinite loading
  const handleStageScroll = (e, stageId) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreLeads(stageId);
    }
  };

  // Search suggestions
  useEffect(() => {
    const searchLeads = async () => {
      if (globalSearch.length < 2) {
        setSearchSuggestions([]);
        return;
      }
      
      try {
        const response = await api.searchLeads(pipelineId, globalSearch, 5);
        setSearchSuggestions(response.leads || []);
      } catch (error) {
        console.error('Error searching leads:', error);
      }
    };
    
    const debounce = setTimeout(searchLeads, 300);
    return () => clearTimeout(debounce);
  }, [globalSearch, pipelineId]);

  // Filter leads
  const filterLeads = (leads) => {
    return leads.filter(lead => {
      // Priority filter
      if (priorityFilter && lead.priority !== priorityFilter) return false;
      
      // Date filter - handle ISO date strings properly
      if (dateFrom || dateTo) {
        const leadDateStr = lead.createdAt?.split('T')[0]; // Get YYYY-MM-DD part
        if (!leadDateStr) return true; // Skip if no date
        
        if (dateFrom && leadDateStr < dateFrom) return false;
        if (dateTo && leadDateStr > dateTo) return false;
      }
      
      // Global search filter
      if (globalSearch.length >= 2) {
        const search = globalSearch.toLowerCase();
        const matchesName = lead.name?.toLowerCase().includes(search);
        const matchesEmail = lead.email?.toLowerCase().includes(search);
        const matchesCompany = lead.company?.toLowerCase().includes(search);
        const matchesPhone = lead.phone?.toLowerCase().includes(search);
        const matchesDoc = lead.documentNumber?.toLowerCase().includes(search);
        
        if (!matchesName && !matchesEmail && !matchesCompany && !matchesPhone && !matchesDoc) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Filter leads by stage search
  const filterStageLeads = (leads, stageId) => {
    const stageSearch = stageSearches[stageId]?.toLowerCase() || '';
    if (!stageSearch) return filterLeads(leads);
    
    return filterLeads(leads).filter(lead => {
      return (
        lead.name?.toLowerCase().includes(stageSearch) ||
        lead.email?.toLowerCase().includes(stageSearch) ||
        lead.company?.toLowerCase().includes(stageSearch)
      );
    });
  };

  // Get currency from settings
  const currency = settings?.currency || 'COP';

  const formatValue = (value) => {
    if (currency === 'COP') {
      if (value >= 1000000) {
        return new Intl.NumberFormat('es-CO', {
          style: 'decimal',
          minimumFractionDigits: 0,
        }).format(value / 1000000) + 'M COP';
      }
      return new Intl.NumberFormat('es-CO', {
        style: 'decimal',
        minimumFractionDigits: 0,
      }).format(value) + ' COP';
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(value);
  };

  const clearFilters = () => {
    setGlobalSearch('');
    setPriorityFilter('');
    setPeriod('all');
    setCustomDateFrom('');
    setCustomDateTo('');
    setStageSearches({});
  };

  const hasActiveFilters = globalSearch || priorityFilter || period !== 'all';

  // Drag handlers
  const handleDragStart = (e, leadId) => {
    setDraggedLeadId(leadId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    setDragOverStageId(stageId);
  };

  const handleDrop = async (e, newStageId) => {
    e.preventDefault();
    if (!draggedLeadId) return;

    // Find the lead in any stage
    let leadToMove = null;
    let oldStageId = null;
    for (const stageId of Object.keys(stageLeadsMap)) {
      const found = stageLeadsMap[stageId].leads.find(l => l.id === draggedLeadId);
      if (found) {
        leadToMove = found;
        oldStageId = stageId;
        break;
      }
    }

    if (leadToMove && leadToMove.stageId !== newStageId) {
      // Check if moving to a "perdido" stage - detect by ID or name
      const targetStage = pipeline?.stages?.find(s => s.id === newStageId);
      const isLostStage = newStageId === 'perdido' || 
                          newStageId.includes('perdido') || 
                          targetStage?.name?.toLowerCase() === 'perdido';
      
      if (isLostStage) {
        setPendingLossMove({
          leadId: draggedLeadId,
          leadName: leadToMove.name,
          oldStageId: oldStageId,
          newStageId: newStageId,
          leadToMove: leadToMove
        });
        setShowLossReasonModal(true);
        setDraggedLeadId(null);
        setDragOverStageId(null);
        return;
      }

      // Normal move (not to perdido)
      await executeLeadMove(leadToMove, oldStageId, newStageId);
    }

    setDraggedLeadId(null);
    setDragOverStageId(null);
  };

  // Function to execute the actual lead move
  const executeLeadMove = async (leadToMove, oldStageId, newStageId, lossReason = null) => {
    const leadId = leadToMove.id;

    // Optimistic update: Move lead visually BEFORE API call
    setStageLeadsMap(prev => {
      const newMap = { ...prev };
      
      // Remove from old stage
      if (oldStageId && newMap[oldStageId]) {
        newMap[oldStageId] = {
          ...newMap[oldStageId],
          leads: newMap[oldStageId].leads.filter(l => l.id !== leadId)
        };
      }
      
      // Add to new stage with updated stageId
      if (newMap[newStageId]) {
        const updatedLead = { ...leadToMove, stageId: newStageId };
        newMap[newStageId] = {
          ...newMap[newStageId],
          leads: [updatedLead, ...newMap[newStageId].leads]
        };
      }
      
      return newMap;
    });

    // Update stage counts optimistically
    setStageCounts(prev => ({
      ...prev,
      [oldStageId]: { ...prev[oldStageId], showing: (prev[oldStageId]?.showing || 1) - 1, total: (prev[oldStageId]?.total || 1) - 1 },
      [newStageId]: { ...prev[newStageId], showing: (prev[newStageId]?.showing || 0) + 1, total: (prev[newStageId]?.total || 0) + 1 }
    }));

    try {
      // Build update data
      const updateData = { stageId: newStageId };
      
      // Add loss reason if provided
      if (lossReason) {
        updateData.lossReason = lossReason.reasonId;
        updateData.lossReasonLabel = lossReason.reasonLabel;
        updateData.lossReasonNote = lossReason.customNote;
        updateData.lostAt = new Date().toISOString();
      }

      await actions.updateLead(leadId, updateData);
      
      if (newStageId === 'perdido') {
        toast.success('Lead marcado como perdido');
      } else {
        toast.success('Lead movido correctamente');
      }
    } catch (error) {
      // Revert optimistic update on error
      setStageLeadsMap(prev => {
        const newMap = { ...prev };
        
        // Remove from new stage
        if (newMap[newStageId]) {
          newMap[newStageId] = {
            ...newMap[newStageId],
            leads: newMap[newStageId].leads.filter(l => l.id !== leadId)
          };
        }
        
        // Add back to old stage
        if (oldStageId && newMap[oldStageId]) {
          newMap[oldStageId] = {
            ...newMap[oldStageId],
            leads: [leadToMove, ...newMap[oldStageId].leads]
          };
        }
        
        return newMap;
      });

      // Revert counts
      setStageCounts(prev => ({
        ...prev,
        [oldStageId]: { ...prev[oldStageId], showing: (prev[oldStageId]?.showing || 0) + 1, total: (prev[oldStageId]?.total || 0) + 1 },
        [newStageId]: { ...prev[newStageId], showing: (prev[newStageId]?.showing || 1) - 1, total: (prev[newStageId]?.total || 1) - 1 }
      }));

      // Show backend validation error
      const errorMessage = error?.response?.data?.detail || 'Error al mover el lead';
      toast.error(errorMessage);
    }
  };

  // Handle loss reason confirmation
  const handleLossReasonConfirm = async (lossReason) => {
    if (pendingLossMove) {
      await executeLeadMove(
        pendingLossMove.leadToMove,
        pendingLossMove.oldStageId,
        pendingLossMove.newStageId,
        lossReason
      );
    }
    setShowLossReasonModal(false);
    setPendingLossMove(null);
  };

  // Handle loss reason cancel
  const handleLossReasonCancel = () => {
    setShowLossReasonModal(false);
    setPendingLossMove(null);
  };

  const handleDragEnd = () => {
    setDraggedLeadId(null);
    setDragOverStageId(null);
  };

  if (state.loading || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!pipeline) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Embudo no encontrado</h2>
          <button onClick={() => navigate('/')} className="text-indigo-600 hover:text-indigo-700 font-medium">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{pipeline.name}</h1>
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {totalLeads} leads
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {formatValue(Object.values(stageLeadsMap).reduce((sum, s) => 
                    sum + (s.leads || []).reduce((acc, l) => acc + (l.dealsTotalValue || 0), 0), 0
                  ))}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => { setSelectedStageId(pipeline.stages[0]?.id); setShowLeadModal(true); }}
            className="hover:opacity-90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            style={{ backgroundColor: primaryColor }}
          >
            <Plus className="w-5 h-5" /> Nuevo Lead
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          {/* Global Search with Autocomplete */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar por nombre, correo, empresa..."
              value={globalSearch}
              onChange={(e) => { setGlobalSearch(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {globalSearch && (
              <button
                onClick={() => setGlobalSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            
            {/* Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchSuggestions.map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => {
                      navigate(`/lead/${lead.id}`);
                      setShowSuggestions(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                      {lead.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{lead.name}</p>
                      <p className="text-xs text-slate-500 truncate">{lead.email} • {lead.company}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              hasActiveFilters
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtros
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
            )}
          </button>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg flex items-center gap-4 flex-wrap">
            {/* Period Filter */}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Periodo</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
              >
                {PERIOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Custom Date Range - only show when period is 'custom' */}
            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Desde</label>
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Hasta</label>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </>
            )}

            {/* Priority Filter */}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Prioridad</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
              >
                <option value="">Todas</option>
                <option value="Alta">Alta</option>
                <option value="Media">Media</option>
                <option value="Baja">Baja</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto p-6">
        <div className="flex gap-6 h-full min-w-max">
          {pipeline.stages?.map((stage) => {
            const stageData = stageLeadsMap[stage.id] || { leads: [], hasMore: false, loading: false };
            const filteredLeads = filterStageLeads(stageData.leads, stage.id);
            const totalStageLeads = stageCounts[stage.id]?.total || 0;
            const stageValue = filteredLeads.reduce((sum, l) => sum + (l.dealsTotalValue || 0), 0);

            return (
              <div
                key={stage.id}
                className={`w-80 flex-shrink-0 bg-slate-100 rounded-xl flex flex-col transition-all ${
                  dragOverStageId === stage.id ? 'ring-2 ring-indigo-500 ring-offset-2' : ''
                }`}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color || '#6366f1' }} />
                      <h3 className="font-semibold text-slate-900">{stage.name}</h3>
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                        {totalStageLeads}
                      </span>
                    </div>
                    <button
                      onClick={() => { setSelectedStageId(stage.id); setShowLeadModal(true); }}
                      className="p-1 hover:bg-slate-200 rounded"
                    >
                      <Plus className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-500">{formatValue(stageValue)}</p>
                  
                  {/* Stage Search */}
                  <div className="mt-2 relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Buscar en esta fase..."
                      value={stageSearches[stage.id] || ''}
                      onChange={(e) => setStageSearches(prev => ({ ...prev, [stage.id]: e.target.value }))}
                      className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Cards */}
                <div
                  className="flex-1 overflow-y-auto p-3 space-y-3"
                  onScroll={(e) => handleStageScroll(e, stage.id)}
                >
                  {filteredLeads.map((lead) => (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => navigate(`/lead/${lead.id}`)}
                      className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition-all ${
                        draggedLeadId === lead.id ? 'opacity-50 rotate-2' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {lead.code && (
                              <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                                {lead.code}
                              </span>
                            )}
                            <h4 className="font-medium text-slate-900 text-sm truncate">{lead.name}</h4>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2 shrink-0">
                          {/* 1er Negocio / Recurrente tag */}
                          {lead.statistics?.totalDeals > 0 ? (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 whitespace-nowrap">
                              Recurrente
                            </span>
                          ) : (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                              1er Negocio
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            lead.priority === 'Alta' ? 'bg-red-100 text-red-700' :
                            lead.priority === 'Media' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {lead.priority}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{lead.company}</p>
                      <p className="text-xs text-slate-400 truncate">{lead.email}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-sm font-semibold text-emerald-600">{formatValue(lead.dealsTotalValue || 0)}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(lead.createdAt).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      {lead.dealCount > 0 && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                          <Briefcase className="w-3 h-3" />
                          {lead.dealCount} negocio{lead.dealCount > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Loading indicator */}
                  {stageData.loading && (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  )}

                  {/* Load more indicator */}
                  {stageData.hasMore && !stageData.loading && filteredLeads.length > 0 && (
                    <div className="text-center py-2 text-xs text-slate-500">
                      Scroll para cargar más...
                    </div>
                  )}

                  {/* Empty state */}
                  {filteredLeads.length === 0 && !stageData.loading && (
                    <div className="text-center py-8 text-slate-400 text-sm">
                      {hasActiveFilters || stageSearches[stage.id]
                        ? 'No hay leads que coincidan'
                        : 'No hay leads en esta etapa'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lead Modal */}
      <LeadFormModal
        isOpen={showLeadModal}
        onClose={() => { setShowLeadModal(false); loadInitialLeads(); }}
        pipelineId={pipelineId}
        stageId={selectedStageId}
      />

      {/* Loss Reason Modal */}
      <LossReasonModal
        isOpen={showLossReasonModal}
        onClose={handleLossReasonCancel}
        onConfirm={handleLossReasonConfirm}
        leadName={pendingLossMove?.leadName}
      />
    </div>
  );
};

export default KanbanBoard;
